import { FC, ReactNode } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography
} from "@mui/material";
import { CounterpartyLookupDetails } from "../../api/Api";
import { Colors } from "../../theme";
import dayjs from "dayjs";

type Props = {
  details: CounterpartyLookupDetails;
};

const fmt = (value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

/** Warehouse dates arrive as ISO strings. Show the date only, the time is not meaningful here. */
const fmtDate = (value?: string | null): ReactNode => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return dayjs(parsed).format("LL");
};

const fmtAmount = (
  amount?: number | null,
  currency?: string | null
): ReactNode => {
  if (amount === null || amount === undefined) return "N/A";
  const formatted = amount.toLocaleString(undefined, {
    maximumFractionDigits: 2
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

const Field: FC<{ label: string; value: ReactNode; wide?: boolean }> = ({
  label,
  value,
  wide = false
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: wide ? "column" : { xs: "column", sm: "row" },
      gap: wide ? 0.25 : { xs: 0.25, sm: 2 },
      paddingY: 0.5
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ flex: wide ? undefined : { sm: "0 0 200px" } }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ flex: 1, whiteSpace: wide ? "pre-wrap" : undefined }}
    >
      {value}
    </Typography>
  </Box>
);

const Section: FC<{
  title: string;
  headerAction?: ReactNode;
  children: ReactNode;
}> = ({ title, headerAction, children }) => (
  // width 100% so a Section placed in the side-by-side row fills its column and both cards end
  // up the same height.
  <Card elevation={3} sx={{ marginBottom: 2, width: "100%" }}>
    <CardContent sx={{ padding: 2, "&:last-child": { paddingBottom: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          marginBottom: 1
        }}
      >
        <Typography variant="h6">{title}</Typography>
        {headerAction}
      </Box>
      <Divider sx={{ marginBottom: 1 }} />
      {children}
    </CardContent>
  </Card>
);

/**
 * Green when the value reads as a settled positive outcome, orange otherwise. Deliberately
 * permissive: the warehouse is free text, so anything unrecognised stays neutral rather than
 * being coloured misleadingly.
 */
const StatusChip: FC<{ value?: string | null }> = ({ value }) => {
  if (!value) return <Chip size="small" label="N/A" />;

  const normalized = value.trim().toLowerCase();
  const isPositive =
    normalized === "approved" ||
    normalized === "completed" ||
    normalized === "complete";
  const isNegative =
    normalized === "rejected" ||
    normalized === "declined" ||
    normalized === "failed";

  if (!isPositive && !isNegative) {
    return <Chip size="small" label={value} />;
  }

  return (
    <Chip
      size="small"
      label={value}
      sx={{
        backgroundColor: isPositive ? Colors.NICE_GREEN : Colors.MFT_ORANGE,
        color: Colors.MFT_DARK_BLUE,
        fontWeight: 600
      }}
    />
  );
};

export const LookupDetailsPanel: FC<Props> = ({ details }) => {
  const { complianceDataAvailable = false, riskDataAvailable = false } =
    details;

  return (
    <Box>
      <Section
        title={details.name ?? "Unnamed counterparty"}
        headerAction={
          details.counterpartyType ? (
            <Chip size="small" label={details.counterpartyType} />
          ) : undefined
        }
      >
        <Field label="Id" value={fmt(details.id)} />
        <Field label="Country" value={fmt(details.country)} />
        <Field
          label="Requesting entity"
          value={fmt(details.requestSubsidiary)}
        />
        <Field label="Request employee" value={fmt(details.requestEmployee)} />
        <Field
          label="Counterparty number"
          value={fmt(details.counterpartyNumber)}
        />
      </Section>

      {/*
        Compliance and Credit risk sit side by side: they are the two independent sources being
        compared, so reading them in parallel beats scrolling between them. Stacks below md.
      */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          gap: { xs: 0, md: 2 }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
          <Section
            title="Compliance"
            headerAction={
              complianceDataAvailable ? undefined : (
                <Chip size="small" label="No compliance record" />
              )
            }
          >
            {complianceDataAvailable ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 0.25, sm: 2 },
                    paddingY: 0.5,
                    alignItems: { sm: "center" }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: { sm: "0 0 200px" } }}
                  >
                    KYC check
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <StatusChip value={details.assessmentStatus} />
                  </Box>
                </Box>
                <Field
                  label="Compliance risk score"
                  value={fmt(details.riskLevel)}
                />
                <Field
                  label="Assessment last modified"
                  value={fmtDate(details.assessmentModifiedOn)}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Compliance holds no KYC record for this counterparty.
              </Typography>
            )}
          </Section>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
          <Section
            title="Credit risk"
            headerAction={
              riskDataAvailable ? undefined : (
                <Chip size="small" label="No credit record" />
              )
            }
          >
            {riskDataAvailable ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 0.25, sm: 2 },
                    paddingY: 0.5,
                    alignItems: { sm: "center" }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: { sm: "0 0 200px" } }}
                  >
                    Credit risk check
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <StatusChip value={details.assessmentType} />
                  </Box>
                </Box>
                <Field label="CreditCatalystID" value={fmt(details.id)} />
                <Field
                  label="Date of credit approval/update"
                  value={fmtDate(details.validFrom)}
                />
                <Field
                  label="Credit limit"
                  value={fmtAmount(details.creditLimit, details.currency)}
                />

                <Field
                  label="Credit rating"
                  value={
                    details.creditRating
                      ? `${details.creditRating}${
                          details.ratingAgency
                            ? ` (${details.ratingAgency})`
                            : ""
                        }`
                      : "N/A"
                  }
                />
                <Field
                  label="Approval note"
                  value={fmt(details.approvalNote)}
                />
              </>
            ) : (
              <Alert severity="info">
                No credit risk record is available for this counterparty. This
                can also mean the credit risk source could not be read.
              </Alert>
            )}
          </Section>
        </Box>
      </Box>

      <Section title="Business">
        <Field
          label="Nature of business"
          value={fmt(details.natureOfBusiness)}
          wide
        />
        <Field label="Product type" value={fmt(details.productType)} wide />
        <Field
          label="Products and services"
          value={fmt(details.productsServices)}
          wide
        />
        <Field label="Notes" value={fmt(details.note)} wide />
      </Section>
    </Box>
  );
};
